import { supertypeClass, property, Persistable, Remoteable, Supertype } from '@haventech/amorphic';
import _ from 'lodash';
import { ExistingPolicy, ExistingPolicyType } from './ExistingPolicy';
import { Customer } from './Customer';
import { Utils } from '../Utils';
import { ExistingPolicies as GQLExistingPolicies } from '@hit/hit-api-client';
import { ExistingPolicyInForce } from './ExistingPolicyInForce';
import { ExistingPolicyApplying } from './ExistingPolicyApplying';
import { ExistingPolicyForDocusign } from '../docusign/ExistingPolicyForDocusign';
import { Assumptions } from '../static/Assumptions';
import { Policy } from './Policy';
import { Exchange1035FundingPurpose } from '../../../coverpath-common/js/interfaces/CoverpathTypes';

@supertypeClass({ toClient: true, toServer: true })
export class ExistingPolicies extends Persistable(Remoteable(Supertype)) implements GQLExistingPolicies {
  @property({
    rule: ['required', 'replacements'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
  })
  replaceInforceApplication: boolean;

  @property({ rule: ['required'], values: ['true', 'false'], descriptions: { true: 'Yes', false: 'No' } })
  hasExistingPolicies: boolean;

  @property({ type: Array, getType: () => ExistingPolicy })
  existingPolicyList: Array<ExistingPolicy> = [];

  @property({
    rule: ['required'],
    values: ['Yes', 'No'],
    descriptions: { Yes: 'Yes', No: 'No' },
    comment: 'Is in the process of applying for any other life insurance or annuities',
  })
  isApplyingForPolicy: string;

  @property({ type: Array, getType: () => ExistingPolicyApplying })
  existingApplicationList: Array<ExistingPolicyApplying> = [];

  @property({ rule: ['required'], values: ['true', 'false'], descriptions: { true: 'Yes', false: 'No' } })
  needMoreCoverage: boolean;

  @property({ rule: ['required', 'currency'] })
  purchaseFaceAmount: number;

  @property({ rule: ['required'], values: ['true', 'false'], descriptions: { true: 'Yes', false: 'No' } })
  hasExistingInforcePolicies: boolean;

  @property({ type: Array, getType: () => ExistingPolicyInForce })
  existingInForcePolicyList: Array<ExistingPolicyInForce> = [];

  @property({ rule: ['required', 'currency', 'numeric'], validate: 'isWithin(0, 10000000)' })
  inForcePoliciesFaceTotal: number;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'determines if this policy going to replace any other existing life insurance policy',
  })
  isReplacementOther: boolean;

  @property({
    rule: ['currency', 'required', 'numericPositive'],
    comment: 'total group insurance coverage amount',
  })
  groupCoverageInForce: number;

  @property({
    rule: ['currency', 'required', 'numericPositive'],
    comment: 'total group insurance coverage amount',
  })
  groupCoverageAppliedFor: number;

  @property({
    rule: ['currency', 'required'],
    validate: 'isWithin(0, 10000000)',
    comment: 'total inforce non-group insurance coverage amount',
  })
  nonGroupCoverageInForce: number;

  @property({
    rule: ['currency', 'required'],
    validate: 'isWithin(0, 10000000)',
    comment: 'total applied for non-group insurance coverage amount',
  })
  nonGroupCoverageAppliedFor: number;

  @property({ getType: () => Customer })
  customer: Customer;

  @property({ toClient: false, toServer: false, persist: false, getType: () => ExistingPolicy })
  _docusign_AllPolicies: Array<ExistingPolicy>;

  @property({ toClient: false, persist: false, getType: () => ExistingPolicy })
  _docusign_replacedOrFundedPolicies: Array<ExistingPolicyForDocusign>;

  @property({ toClient: false, persist: false, getType: () => ExistingPolicy })
  _docusign_externalReplacedOrFundedPolicies: Array<ExistingPolicyForDocusign>;

  @property({ toClient: false, persist: false, getType: () => ExistingPolicy })
  _docusign_internalReplacedOrFundedPolicies: Array<ExistingPolicyForDocusign>;

  @property({ toClient: false, persist: false })
  _docusign_replacedOrFundedPoliciesFirst: ExistingPolicy;

  @property({ toClient: false, persist: false })
  _docusign_replacedOrFundedPoliciesSecond: ExistingPolicy;

  @property({ toClient: false, persist: false })
  _docusign_replacedOrFundedPoliciesThird: ExistingPolicy;

  constructor() {
    super();
  }

  static new(customer) {
    return new ExistingPolicies().init(customer);
  }

  init(customer) {
    this.customer = customer;
    return this;
  }

  hasExistingPoliciesTrigger() {
    if (
      this.customer.application &&
      this.customer.application.primaryPolicy &&
      this.customer.application.primaryPolicy.insured.existingPolicies.hasExistingPolicies
    ) {
      if (this.customer.application.primaryPolicy.insured.existingPolicies.existingPolicyList.length === 0) {
        this.customer.application.primaryPolicy.insured.existingPolicies.existingPolicyList.push(
          ExistingPolicy.new(this.customer)
        );
      }
    } else {
      this.customer.application.primaryPolicy.insured.existingPolicies.existingPolicyList = [];
    }
  }

  hasExistingInforcePoliciesTrigger() {
    const existingPolicies = this.customer.application.primaryPolicy.insured.existingPolicies;
    if (existingPolicies.hasExistingInforcePolicies) {
      existingPolicies.hasExistingPolicies = true;
      if (existingPolicies.existingInForcePolicyList.length === 0) {
        // Initialize first iterate-collect element when user clicks 'Yes'
        existingPolicies.existingInForcePolicyList.push(ExistingPolicyInForce.new(this.customer));
      }
    } else {
      if (!this.hasExistingOrReplacementPolicy()) {
        this.customer.application.primaryPolicy.insured.existingPolicies.hasExistingPolicies = false;
      }
      if (this.existingInForcePolicyList.some((policy) => policy.cashValueTransfer > 0)) {
        this.customer.application.primaryPolicy.removeExchange1035();
      }
      existingPolicies.existingInForcePolicyList = [];
    }
  }

  replaceInforceApplicationTrigger() {
    const existingPolicies = this.customer.application.primaryPolicy.insured.existingPolicies;
    if (existingPolicies.replaceInforceApplication) {
      existingPolicies.hasExistingPolicies = true;
      if (existingPolicies.existingPolicyList.length === 0) {
        // Initialize first iterate-collect element when user clicks 'Yes'
        this.existingPolicyListAdd();
      }
    } else {
      if (!this.hasExistingOrReplacementPolicy()) {
        existingPolicies.hasExistingPolicies = false;
      }
      if (this.existingPolicyList.some((policy) => policy.cashValueTransfer > 0)) {
        this.customer.application.primaryPolicy.removeExchange1035();
      }
      existingPolicies.existingPolicyList = [];
    }
  }

  isApplyingForPolicyTrigger() {
    if (
      this.customer.application &&
      this.customer.application.primaryPolicy &&
      this.customer.application.primaryPolicy.insured.existingPolicies.isApplyingForPolicy === 'Yes'
    ) {
      if (this.customer.application.primaryPolicy.insured.existingPolicies.existingApplicationList.length === 0) {
        this.customer.application.primaryPolicy.insured.existingPolicies.existingApplicationList.push(
          ExistingPolicyApplying.new(this.customer)
        );
      }
    } else {
      this.customer.application.primaryPolicy.insured.existingPolicies.existingApplicationList = [];
    }
  }

  existingPolicyListAdd() {
    //override Add Item in iterate-collect so that isReplacement can be set
    const policy = ExistingPolicy.new(this.customer);
    policy.isReplacement = true;
    this.existingPolicyList.push(policy);
  }

  hasExistingOrReplacementPolicy() {
    return (
      (this.customer.application &&
        this.customer.application.primaryPolicy &&
        this.customer.application.primaryPolicy.insured.existingPolicies.hasExistingInforcePolicies) ||
      (this.customer.application &&
        this.customer.application.primaryPolicy &&
        this.customer.application.primaryPolicy.insured.existingPolicies.replaceInforceApplication)
    );
  }

  get AllPolicies(): Array<ExistingPolicy> {
    this._docusign_AllPolicies = [
      ...this.existingPolicyList,
      ...this.existingApplicationList,
      ...this.existingInForcePolicyList,
    ];

    while (this._docusign_AllPolicies.length < 6) {
      this._docusign_AllPolicies.push(new ExistingPolicy());
    }

    return this._docusign_AllPolicies;
  }

  getPolicyApplyingForTotal() {
    const policies = this.existingApplicationList;
    let total = 0;

    for (let ix = 0; ix < policies.length; ++ix) {
      total += policies[ix].face;
    }

    total += this.customer.application.primaryPolicy.faceAmount;

    return Utils.formatNumber(total);
  }

  getInForcePolicyTotal() {
    const policies = this.existingInForcePolicyList;
    let total = 0;

    for (let ix = 0; ix < policies.length; ++ix) {
      total += policies[ix].face;
    }

    return total > 0 ? Utils.formatNumber(total) : '0';
  }

  isTerminateExistingPolicy(): boolean {
    const policies = this.AllPolicies;
    return policies.some((policy) => policy.isReplacement);
  }

  isUseExistingPolicyFund(): boolean {
    const exchange1035 = this.customer?.application?.primaryPolicy?.alirRider?.exchange1035;
    return exchange1035?.applyTowards === Exchange1035FundingPurpose.PREMIUM_AND_ALIR;
  }

  /**
   * replacedOrFundedPolicies will NOT contain replaced and grouped policy since these are not treated as replacement policies.
   * With an exception of: If application state is KS, MI, NY or WA grouped policies are still consisdered as replaced policies.
   */
  get replacedOrFundedPolicies(): Array<ExistingPolicyForDocusign> {
    const currentPolicy = this.customer.application.primaryPolicy;
    const policies = this.AllPolicies;
    const filtered = policies.filter(
      (policy) =>
        (policy.isReplacement && (!policy.isGroupPolicy || !this.shouldSkipReplacementByAppState())) ||
        (policy.isNWMReplacement() && currentPolicy.arePolicyAgentsOnWatchForNWMReplacement()) ||
        policy.isReplacementFunds
    );
    const policiesForDocusign: Array<ExistingPolicyForDocusign> = [];
    return filtered.map((ePolicy) => {
      const forDocusign = ePolicy as ExistingPolicyForDocusign;

      forDocusign.isReplacedAbbrev = ePolicy.isReplacement ? 'R' : '';
      forDocusign.isFundedAbbrev = ePolicy.isReplacementFunds ? 'F' : '';

      if (ePolicy.insured === 'other') {
        forDocusign.insuredName = ePolicy.insuredName;
      } else {
        forDocusign.insuredName = this.customer.application.primaryPolicy.insured.person.getFullName();
      }

      return forDocusign;
    });
  }

  get externalReplacedOrFundedPolicies(): Array<ExistingPolicyForDocusign> {
    return this.replacedOrFundedPolicies.filter((policy) => policy.companyName !== 'MassMutual');
  }

  get internalReplacedOrFundedPolicies(): Array<ExistingPolicyForDocusign> {
    return this.replacedOrFundedPolicies.filter((policy) => policy.companyName === 'MassMutual');
  }

  get existingInForcePolicyListHasReplacement(): boolean {
    return this.existingInForcePolicyList.some((policy) => policy.isReplacement);
  }

  replacedOrFundedPoliciesList(currentPolicy: Policy): Array<ExistingPolicyForDocusign> {
    const policies = this.AllPolicies;
    const filtered = policies.filter(
      (policy) =>
        (policy.isReplacement &&
          (!policy.isGroupPolicy || !this.shouldSkipReplacementByAppState(currentPolicy.getApplicationState()))) ||
        (policy.isNWMReplacement() && currentPolicy.arePolicyAgentsOnWatchForNWMReplacement()) ||
        policy.isReplacementFunds
    );
    const policiesForDocusign: Array<ExistingPolicyForDocusign> = [];
    return filtered.map((ePolicy) => {
      const forDocusign = ePolicy as ExistingPolicyForDocusign;

      forDocusign.isReplacedAbbrev = ePolicy.isReplacement ? 'R' : '';
      forDocusign.isFundedAbbrev = ePolicy.isReplacementFunds ? 'F' : '';

      if (ePolicy.insured === 'other') {
        forDocusign.insuredName = ePolicy.insuredName;
      } else {
        forDocusign.insuredName = currentPolicy.insured.person.getFullName();
      }

      return forDocusign;
    });
  }

  replacedOrFundedPolicyByIndex(index: number): ExistingPolicy {
    const existingPolicies = this.replacedOrFundedPolicies;
    return existingPolicies[index];
  }

  hasSecondInsurer(): boolean {
    return this.replacedOrFundedPolicies.length >= 2;
  }

  hasThirdInsurer(): boolean {
    return this.replacedOrFundedPolicies.length >= 3;
  }

  get replacedOrFundedPoliciesFirst(): ExistingPolicy {
    return this.replacedOrFundedPolicies[0];
  }

  get replacedOrFundedPoliciesSecond(): ExistingPolicy {
    if (this.hasSecondInsurer()) {
      return this.replacedOrFundedPolicies[1];
    }

    return undefined;
  }

  //@property({toClient: false, persist: false})
  get replacedOrFundedPoliciesThird(): ExistingPolicy {
    if (this.hasThirdInsurer()) {
      return this.replacedOrFundedPolicies[2];
    }

    return undefined;
  }

  /**
   * GroupPolicies for most state (except NY, WA, KS & MI) skips replacement WF
   */
  private shouldSkipReplacementByAppState(state?) {
    const appState = state ? state : this.customer.application.primaryPolicy.getApplicationState();
    if (Assumptions.groupReplacementStates.includes(appState)) {
      return false;
    }

    return true;
  }

  /**
   * Determines if it skip replacement workflow i.e. MA4 or TC4
   */
  shouldSkipReplacementOnExistingPolicies(currentPolicy: Policy): boolean {
    //respect replaceInforceApplication=true above all else if using an old/non-2019 filing
    if (!this.customer.isCoverpath2019MetadataEnabled(currentPolicy) && this.replaceInforceApplication) {
      return false;
    }

    return [...this.existingPolicyList, ...this.existingInForcePolicyList]
      .filter((policy) => policy.isReplacement)
      .every(
        (policy) =>
          policy.isGroupPolicy &&
          this.shouldSkipReplacementByAppState(currentPolicy.getApplicationState()) &&
          (!policy.isNWMReplacement() || !currentPolicy.arePolicyAgentsOnWatchForNWMReplacement())
      );
  }

  get internalTermReplacedPolicies(): Array<ExistingPolicy> {
    // Internal Replacement is not allowed for WholeLife
    if (
      this.customer.application.primaryPolicy.isWholeLifePolicy() &&
      !this.customer.application.primaryPolicy.isPartOfJointApp()
    ) {
      return [];
    }

    return this.AllPolicies.filter(
      (policy) =>
        policy.isReplacement &&
        (!policy.isGroupPolicy || !this.shouldSkipReplacementByAppState()) &&
        policy.companyName === 'MassMutual' &&
        policy.type === 'term'
    );
  }

  internalTermReplacedPolicyByIndex(index: number): ExistingPolicy {
    const internalPolicy = this.internalTermReplacedPolicies[index];

    return internalPolicy;
  }

  getTotalCashTransferValue(): number {
    return this.hasCashValueReplacement() ? this.getCashValueReplacement().cashValueTransfer : 0;
  }

  getTotalCostBasis(): number {
    return this.hasCashValueReplacement() ? this.getCashValueReplacement().costBasis : null;
  }

  hasMec(): boolean {
    return this.hasCashValueReplacement() ? this.getCashValueReplacement().isMec : null;
  }

  has1035Exchange(): boolean {
    return this.hasCashValueReplacement();
  }

  hasNWMReplacement(): boolean {
    const policies = this.AllPolicies;
    return policies.some((policy) => policy.isNWMReplacement());
  }

  getNWMReplacement(): ExistingPolicy {
    return this.AllPolicies.find((policy) => policy.isNWMReplacement());
  }

  private getNWMReplacementPolicies(): Array<ExistingPolicy> {
    return this.AllPolicies.filter((policy) => policy.isNWMReplacement());
  }

  getNWMReplacementPolicyNumbers() {
    const numbers = [];
    this.getNWMReplacementPolicies().forEach((policy: ExistingPolicy) => numbers.push(policy.number));
    return numbers.join(', ').replace(/, ([^,]*)$/, ' & $1');
  }

  getNWMReplacementPolicyTypes() {
    const types = [];
    this.getNWMReplacementPolicies().forEach((policy: ExistingPolicy) => types.push(policy.type));
    return types.join(', ').replace(/, ([^,]*)$/, ' & $1');
  }

  hasCashValueReplacement(): boolean {
    return !!this.getCashValueReplacement();
  }

  getCashValueReplacement(): ExistingPolicy {
    const replacements = [].concat(this.existingInForcePolicyList).concat(this.existingPolicyList);
    return replacements.find((replacement) => replacement && replacement.cashValueTransfer > 0);
  }

  removeCashValueReplacement() {
    const cvReplacement = this.getCashValueReplacement();
    const removeCvReplacementFromArray = (arr: ExistingPolicy[]) => {
      for (let i = 0; i < arr.length; i++) {
        if (arr[i] === cvReplacement) {
          arr[i].cashValueTransfer = 0;
        }
      }
    };
    if (cvReplacement) {
      removeCvReplacementFromArray(this.existingInForcePolicyList);
      removeCvReplacementFromArray(this.existingPolicyList);
    }
  }

  addCashValueReplacement(cashValueTransferAmount: number, type?: ExistingPolicyType) {
    if (!this.hasCashValueReplacement()) {
      const newReplacement = ExistingPolicyInForce.new(this.customer);
      newReplacement.isReplacement = true;
      newReplacement.is1035Exchange = true;
      newReplacement.type = type || ExistingPolicyType.WHOLE;
      newReplacement.cashValueTransfer = cashValueTransferAmount;
      newReplacement.owner = 'owner';
      newReplacement.replacementLoanAmount = 0;
      this.existingInForcePolicyList.push(newReplacement);
      this.hasExistingInforcePolicies = true;
      this.hasExistingPolicies = true;
    }
  }

  existingPolicyListRemove(index) {
    const policy = this.customer.application.primaryPolicy;
    const deleteThisExistingPolicy = this.existingPolicyList[index];

    this.existingPolicyList.splice(index, 1);
    if (deleteThisExistingPolicy.cashValueTransfer > 0 && policy.isWholeLifePolicy()) {
      policy.removeExchange1035();
      if (!policy.hasAlirScheduledPayments && !policy.alirLumpSumUses1035 && !policy.hasAlirLumpSum) {
        policy.hasAlirRider = false; // untick ALIR rider in policy info section if no children are ticked
      }
    }

    if (this.existingPolicyList.length === 0) {
      this.replaceInforceApplication = false;
    }
  }

  existingInForcePolicyListRemove(index) {
    const policy = this.customer.application.primaryPolicy;
    const deleteThisExistingPolicy = this.existingInForcePolicyList[index];

    this.existingInForcePolicyList.splice(index, 1);
    if (deleteThisExistingPolicy.cashValueTransfer > 0 && policy.isWholeLifePolicy()) {
      policy.removeExchange1035();
      if (!policy.hasAlirScheduledPayments && !policy.alirLumpSumUses1035 && !policy.hasAlirLumpSum) {
        policy.hasAlirRider = false; // untick ALIR rider in policy info section if no children are ticked
      }
    }

    if (this.existingInForcePolicyList.length === 0) {
      this.hasExistingInforcePolicies = false;
    }
  }
}
